// client/src/ApplicationsTable/AppsTable.jsx
import React, { useState, useEffect } from "react";
import "./ApplicationsTable.css";
import JobModal from "./JobModal";
import ContactHeatmap from "./ContactHeatmap";
import FollowUpNotes from "./FollowUpNotes";
import Analysis from "./Analysis"; // adjust path if needed
import SkillTracker from "./SkillTracker";




const API_BASE = "https://job.scae.co";

const defaultNewJob = {
  company: "",
  job_title: "",
  job_link: "",
  job_page: "",
  location: "",
  salary: "",
  job_source: "",
  date_applied: "", // set when clicking Add New Job
  resume_version: "",
  cover_letter_used: "",
  status: "",
  recruiter_name: "",
  recruiter_email: "",
  last_contact_date: "",
  next_followup_date: "",
  notes: "",
  resume_file_name: "",
  cover_letter_file_name: "",
  skills: [],
  tools: [],
  keywords: [],
  matchScore: null,
  missing: [],
  matched: [],
  email_massage:'',
};

const computeDashboardStats = (apps, todayStr) => {
  const stats = {
    total: apps.length,
    appliedThisWeek: 0,
    inProcess: 0,
    offers: 0,
    rejected: 0,
    overdueFollowups: 0,
    upcomingFollowups: 0,
  };

  apps.forEach((job) => {
    // Applied in last 7 days
    if (job.date_applied) {
      const daysSinceApplied = diffInDays(job.date_applied, todayStr); // positive = past
      if (daysSinceApplied >= 0 && daysSinceApplied <= 7) {
        stats.appliedThisWeek += 1;
      }
    }

    // Status buckets
    if (job.status === "Offer") {
      stats.offers += 1;
    } else if (job.status === "Rejected" || job.status === "Ghosted") {
      stats.rejected += 1;
    } else {
      // everything else = in process
      stats.inProcess += 1;
    }

    // Followups
    if (job.next_followup_date) {
      const daysUntil = diffInDays(todayStr, job.next_followup_date); 
      // diffInDays(from, to)
      // if to < from → negative → overdue
      if (daysUntil < 0) {
        stats.overdueFollowups += 1;
      } else if (daysUntil >= 0 && daysUntil <= 7) {
        stats.upcomingFollowups += 1;
      }
    }
  });

  return stats;
};

const computeStatusBreakdown = (apps) => {
  const counts = {
    Saved: 0,
    Applied: 0,
    "Recruiter Contacted": 0,
    "Assessment Sent": 0,
    "Phone Screen": 0,
    "Technical Interview": 0,
    "Final Interview": 0,
    Offer: 0,
    Rejected: 0,
    Ghosted: 0,
  };

  apps.forEach((job) => {
    if (job.status && counts[job.status] !== undefined) {
      counts[job.status] += 1;
    }
  });

  const total = apps.length || 1; // avoid divide by zero

  const segments = Object.entries(counts)
    .filter(([_, v]) => v > 0)
    .map(([status, count]) => ({
      status,
      count,
      percent: (count / total) * 100,
    }));

  return { counts, total, segments };
};

const getUpcomingFollowups = (apps, todayStr, limit = 5) => {
  return apps
    .filter((job) => job.next_followup_date)
    .map((job) => {
      const daysUntil = diffInDays(todayStr, job.next_followup_date);
      return { ...job, daysUntil };
    })
    .filter((job) => job.daysUntil >= 0) // keep today or future
    .sort((a, b) => a.daysUntil - b.daysUntil)
    .slice(0, limit);
};


function calculateNextFollowup(status, dateApplied) {
  if (!dateApplied) return null;

  const base = new Date(dateApplied);
  if (isNaN(base)) return null;

  let daysToAdd = null;

  switch (status) {
    case "Applied":
      daysToAdd = 5;
      break;
    case "Recruiter Contacted":
      daysToAdd = 3;
      break;
    case "Assessment Sent":
    case "Phone Screen":
    case "Technical Interview":
      daysToAdd = 2;
      break;
    case "Final Interview":
      daysToAdd = 1;
      break;
    case "Offer":
    case "Rejected":
    case "Ghosted":
      return null;
    default:
      return null;
  }

  if (daysToAdd == null) return null;

  const next = new Date(base);
  next.setDate(base.getDate() + daysToAdd);

  // return yyyy-mm-dd
  return next.toISOString().slice(0, 10);
}

const getStatusClass = (status) => {
  switch (status) {
    case "Saved":
      return "status-saved";
    case "Applied":
      return "status-applied";
    case "Recruiter Contacted":
      return "status-recruiter";
    case "Assessment Sent":
      return "status-assessment";
    case "Phone Screen":
      return "status-phone";
    case "Technical Interview":
      return "status-technical";
    case "Final Interview":
      return "status-final";
    case "Offer":
      return "status-offer";
    case "Rejected":
      return "status-rejected";
    case "Ghosted":
      return "status-ghosted";
    default:
      return "";
  }
};

const formatToday = () => new Date().toISOString().slice(0, 10);

const diffInDays = (from, to) => {
  if (!from || !to) return "";
  const d1 = new Date(from);
  const d2 = new Date(to);
  if (isNaN(d1) || isNaN(d2)) return "";
  const ms = d2 - d1;
  return Math.round(ms / (1000 * 60 * 60 * 24));
};

function getFollowupClass(nextFollowupDate, todayStr) {
  if (!nextFollowupDate) return "";

  // normalize to midnight to avoid timezone weirdness
  const d = new Date(`${nextFollowupDate}T00:00:00`);
  const t = new Date(`${todayStr}T00:00:00`);

  if (isNaN(d) || isNaN(t)) return "";

  if (d.getTime() < t.getTime()) {
    return "followup-overdue";      // past
  } else if (d.getTime() === t.getTime()) {
    return "followup-today";        // today
  } else {
    return "followup-upcoming";     // future (soft highlight or normal)
  }
}


const detectJobSource = (url) => {
  try {
    const u = new URL(url);
    const host = u.hostname.toLowerCase();
    if (host.includes("linkedin")) return "LinkedIn";
    if (host.includes("indeed")) return "Indeed";
    if (host.includes("glassdoor")) return "Glassdoor";
    if (host.includes("google")) return "Google Jobs";
    return "Company Site";
  } catch {
    return "";
  }
};

function formatToYMD(dateValue) {
  if (!dateValue) return null;
  const d = new Date(dateValue);
  if (isNaN(d)) return null;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

const AppsTable = () => {
  const [apps, setApps] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isNewJob, setIsNewJob] = useState(false);
  const [editDraft, setEditDraft] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  const todayStr = formatToday();
  const stats = computeDashboardStats(apps, todayStr);
  const statusBreakdown = computeStatusBreakdown(apps);
  const upcomingFollowups = getUpcomingFollowups(apps, todayStr, 5);

  const selectedJob = apps.find((job) => job.id === selectedJobId) || null;
  const activeJob =
    (isEditing || isNewJob) && editDraft ? editDraft : selectedJob;

  // ===== LOAD FROM BACKEND =====
  const loadJobs = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/jobs`);
      const data = await res.json();
      setApps(data);
    } catch (err) {
      console.error("Failed to load jobs:", err);
    }
  };

  useEffect(() => {
    loadJobs();
  }, []);

  /** Open existing job modal */
  const openModal = (job) => {
    setSelectedJobId(job.id);
    setIsEditing(false);
    setIsNewJob(false);
    setEditDraft(null);
  };

  /** Open NEW job modal */
  const openNewJobModal = () => {
    const today = formatToday();
    setSelectedJobId(null);
    setIsEditing(true);
    setIsNewJob(true);
    setEditDraft({
      ...defaultNewJob,
      date_applied: today,
    });
  };

  /** Close modal */
  const closeModal = () => {
    setSelectedJobId(null);
    setIsEditing(false);
    setIsNewJob(false);
    setEditDraft(null);
  };

  /** Start editing existing Job */
  const startEdit = () => {
    if (!selectedJob) return;
    setIsEditing(true);
    setIsNewJob(false);
    setEditDraft({ ...selectedJob });
  };

  /** Cancel edit/new */
  const cancelEdit = () => {
    if (isNewJob) {
      closeModal();
      return;
    }
    setIsEditing(false);
    setEditDraft(null);
  };

  /** Save, either create or update (call backend) */
const saveEdit = async () => {
  if (!editDraft) return;

  const isCreate = isNewJob || !editDraft.id;   // ✅ safety

  try {
    if (isCreate) {
      // CREATE
      const res = await fetch(`${API_BASE}/api/jobs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editDraft),
      });

      if (!res.ok) {
        const txt = await res.text();
        console.error("Create failed:", txt);
        alert("Error saving job (create). Check server logs.");
        return;
      }

      const saved = await res.json();
      setApps((prev) => [saved, ...prev]);
      setIsNewJob(false);
      setIsEditing(false);
      setEditDraft(null);
      setSelectedJobId(saved.id);
    } else {
      // UPDATE
      const res = await fetch(`${API_BASE}/api/jobs/${editDraft.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editDraft),
      });

      if (!res.ok) {
        const txt = await res.text();
        console.error("Update failed:", txt);
        alert("Error saving job (update). Check server logs.");
        return;
      }

      const updated = await res.json();
      setApps((prev) =>
        prev.map((job) => (job.id === updated.id ? updated : job))
      );
      setIsEditing(false);
      setEditDraft(null);
      setSelectedJobId(updated.id);
    }
  } catch (err) {
    console.error("Save failed:", err);
    alert("Save failed – see console for details.");
  }
};


  /** Delete job (backend + frontend) */
  const deleteJob = async () => {
    if (!activeJob || !activeJob.id) return;

    try {
      await fetch(`${API_BASE}/api/jobs/${activeJob.id}`, {
        method: "DELETE",
      });
      setApps((prev) => prev.filter((job) => job.id !== activeJob.id));
      closeModal();
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  /** Handle text field / select changes in draft */
  const handleDraftChange = (field, value) => {
    setEditDraft((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, [field]: value };

      if (field === "job_link") {
        const autoSource = detectJobSource(value);
        if (autoSource) updated.job_source = autoSource;
      }
      if (field === "status") {
      const nextFollow = calculateNextFollowup(value, prev.date_applied);
      updated.next_followup_date = nextFollow;
    }

      return updated;
    });
  };

  /** Handle file uploads (store file name only) */
  const handleFileChange = (field, file) => {
    const fileName = file ? file.name : "";
    handleDraftChange(field, fileName);
  };

  /** Status change from table → persist to backend */
  const updateStatusFromTable = async (id, status) => {
    const job = apps.find((j) => j.id === id);
    if (!job) return;

    const nextFollow = calculateNextFollowup(status, job.date_applied);

    const updatedJob = { 
      ...job, 
      status,
      next_followup_date: nextFollow 
    };

    try {
      const res = await fetch(`${API_BASE}/api/jobs/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updatedJob),
      });
      const saved = await res.json();
      setApps((prev) => prev.map((j) => (j.id === saved.id ? saved : j)));
    } catch (err) {
      console.error("Status update failed:", err);
    }
  };


// ===== Paste JSON → merge into editDraft/selected job =====
const generateAIKeywords = (input) => {
  let extracted;

  // 1) Accept string or object
  try {
    if (typeof input === "string") {
      extracted = JSON.parse(input);
    } else {
      extracted = input;
    }
  } catch (e) {
    console.error("Invalid JSON for AI extract:", e);
    alert("Invalid JSON. Check the format.");
    return;
  }

  if (!extracted) {
    alert("No extracted data provided.");
    return;
  }

  const mergeIfEmptyFields = [
    "company",
    "job_title",
    "job_link",
    "job_page",
    "location",
    "salary",
    "job_source",
    "recruiter_name",
    "recruiter_email",
    "job_description",
  ];

  // ✅ Only make sure we are in edit mode – do NOT change isNewJob
  setIsEditing(true);

  setEditDraft((prev) => {
    const base =
      prev ||
      (selectedJob
        ? { ...selectedJob }
        : { ...defaultNewJob, date_applied: formatToday() });

    const merged = { ...base };

    mergeIfEmptyFields.forEach((field) => {
      const current = merged[field];
      const incoming = extracted[field];

      if (
        (current === null ||
          current === undefined ||
          current === "" ||
          current === 0) &&
        incoming != null &&
        incoming !== ""
      ) {
        merged[field] = incoming;
      }
    });

    // Arrays
    merged.skills = extracted.skills || [];
    merged.tools = extracted.tools || [];
    merged.keywords = extracted.keywords || [];

    // Match fields
    merged.matchScore =
      extracted.matchScore ??
      extracted.match_score ??
      null;

    merged.missing =
      extracted.missing ??
      extracted.missing_skills ??
      [];

    merged.matched = extracted.matched ?? [];
    merged.email_massage = extracted.email_massage ?? [];

    return merged;
  });
};

const generateAIKeywordsFromString = (jsonString) => {
  try {
    const extracted = JSON.parse(jsonString);
    generateAIKeywords(extracted);
  } catch (e) {
    console.error("Invalid JSON:", e);
    alert("Invalid JSON. Check the format.");
  }
};





  /** Search + filter list */
  const filteredApps = apps.filter((job) => {
    const term = searchTerm.toLowerCase();
    const matchesSearch =
      !term ||
      [job.company, job.job_title, job.location, job.job_source]
        .filter(Boolean)
        .some((field) => field.toLowerCase().includes(term));

    const matchesStatus =
      statusFilter === "All" || job.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  return (
    <div className="apps-wrapper">
      <h2 className="apps-title">Applications History</h2>

      <button
        className="btn btn-primary add-job-btn"
        onClick={openNewJobModal}
      >
        + Add New Job
      </button>

      {/* Search + Filter */}
      <div className="toolbar">
        <input
          className="search-input"
          placeholder="Search by company, job title, location, source..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        <select
          className="status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="All">All statuses</option>
          <option value="Saved">Saved</option>
          <option value="Applied">Applied</option>
          <option value="Recruiter Contacted">Recruiter Contacted</option>
          <option value="Assessment Sent">Assessment Sent</option>
          <option value="Phone Screen">Phone Screen</option>
          <option value="Technical Interview">Technical Interview</option>
          <option value="Final Interview">Final Interview</option>
          <option value="Offer">Offer</option>
          <option value="Rejected">Rejected</option>
          <option value="Ghosted">Ghosted</option>
        </select>
      </div>
      <div className="table-wrapper">

        <table className="apps-table">
          <thead>
            <tr>
              <th>Company</th>
              <th>Job Title</th>
              <th>Status</th>
              <th>Match Score %</th>
              <th>Date Applied</th>
              <th>Days Since</th>
              <th>Last Contact </th>
              <th>Next Follow-up Date</th>
              <th>Source</th>
              <th>Location</th>
              
            </tr>
          </thead>

          <tbody>
            {filteredApps.map((app) => {
              const daysSince = diffInDays(app.date_applied, todayStr);
              return (
                <tr
                  key={app.id}
                  className="row-clickable"
                  onClick={() => openModal(app)}
                >
                  <td>{app.company}</td>
                  <td>{app.job_title}</td>

                  <td>
                    <select
                      value={app.status}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) =>
                        updateStatusFromTable(app.id, e.target.value)
                      }
                      className={`status-select ${getStatusClass(app.status)}`}
                    >
                      <option value="Saved">Saved</option>
                      <option value="Applied">Applied</option>
                      <option value="Recruiter Contacted">
                        Recruiter Contacted
                      </option>
                      <option value="Assessment Sent">Assessment Sent</option>
                      <option value="Phone Screen">Phone Screen</option>
                      <option value="Technical Interview">
                        Technical Interview
                      </option>
                      <option value="Final Interview">Final Interview</option>
                      <option value="Offer">Offer</option>
                      <option value="Rejected">Rejected</option>
                      <option value="Ghosted">Ghosted</option>
                    </select>
                  </td>
                  <td>{app.matchScore}</td>   
                  <td>{formatToYMD(app.date_applied)}</td>
                  <td>{daysSince !== "" ? daysSince : "-"}</td>
                  <td
                    style={{
                      color: !formatToYMD(app.last_contact_date) ? "red" : "inherit",
                      fontWeight: !formatToYMD(app.last_contact_date) ? "600" : "normal"
                    }}
                  >
                    {formatToYMD(app.last_contact_date) || "No contact"}
                  </td>
                  <td>
                      {formatToYMD(app.next_followup_date) ? (
                        <span
                          className={
                            "followup-badge " +
                            getFollowupClass(app.next_followup_date, todayStr)
                          }
                        >
                          {formatToYMD(app.next_followup_date)}
                        </span>
                      ) : (
                        "-"
                      )}
                    </td>
                  <td>{app.job_source}</td>
                  <td>{app.location}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

      </div>

      {/* DASHBOARD SUMMARY */}
      <div className="dashboard">
        <div className="dash-card">
          <div className="dash-label">Total Applications</div>
          <div className="dash-value">{stats.total}</div>
        </div>

        <div className="dash-card">
          <div className="dash-label">In Process</div>
          <div className="dash-value">{stats.inProcess}</div>
        </div>

        <div className="dash-card">
          <div className="dash-label">Offers</div>
          <div className="dash-value dash-value-positive">{stats.offers}</div>
        </div>

        <div className="dash-card">
          <div className="dash-label">Rejected / Ghosted</div>
          <div className="dash-value dash-value-negative">{stats.rejected}</div>
        </div>

        <div className="dash-card">
          <div className="dash-label">Applied Last 7 Days</div>
          <div className="dash-value">{stats.appliedThisWeek}</div>
        </div>

        <div className="dash-card">
          <div className="dash-label">Overdue Follow-ups</div>
          <div className="dash-value dash-value-warning">
            {stats.overdueFollowups}
          </div>
          <div className="dash-sub">
            Upcoming (7 days): {stats.upcomingFollowups}
          </div>
        </div>
      </div>
      {/* STATUS BAR + UPCOMING FOLLOW-UPS */}
    <div className="dashboard-row-2">
      {/* Status Distribution Bar */}
      <div className="dash-panel">
        <div className="dash-panel-header">
          <span className="dash-panel-title">Status Distribution</span>
          <span className="dash-panel-sub">
            Total: {statusBreakdown.total}
          </span>
        </div>

        <div className="status-bar">
          {statusBreakdown.segments.length === 0 ? (
            <span className="status-bar-empty">No applications yet</span>
          ) : (
            statusBreakdown.segments.map((seg) => (
              <div
                key={seg.status}
                className={`status-bar-segment status-${seg.status
                  .toLowerCase()
                  .replace(/\s+/g, "-")}`}
                style={{ width: `${seg.percent}%` }}
                title={`${seg.status}: ${seg.count}`}
              >
                {seg.percent > 10 && (
                  <span className="status-bar-label">
                    {seg.status} ({seg.count})
                  </span>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Upcoming Follow-ups */}
        <div className="dash-panel">
          <div className="dash-panel-header">
            <span className="dash-panel-title">Upcoming Follow-ups ({upcomingFollowups.length})</span>
            <span className="dash-panel-sub">
              Next 7 days highlighted
            </span>
          </div>

          {upcomingFollowups.length === 0 ? (
            <div className="followup-empty">
              No upcoming follow-ups scheduled.
            </div>
          ) : (
            <ul className="followup-list">
              {upcomingFollowups.map((job) => {
                const daysUntil = job.daysUntil;
                let badgeClass = "followup-badge";
                let badgeText = "";

                if (daysUntil === 0) {
                  badgeClass += " followup-today";
                  badgeText = "Today";
                } else if (daysUntil <= 7) {
                  badgeClass += " followup-soon";
                  badgeText = `${daysUntil} days`;
                } else {
                  badgeText = `${daysUntil} days`;
                }

                return (
                  <li key={job.id} className="followup-item">
                    <div className="followup-main">
                      <div className="followup-title">
                        {job.company} — {job.job_title}
                      </div>
                      <div className="followup-meta">
                        {formatToYMD(job.next_followup_date)} ·{" "}
                        <span className={badgeClass}>{badgeText}</span>
                      </div>
                    </div>
                    <button
                      className="followup-open-btn"
                      onClick={() => openModal(job)}
                    >
                      Open
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>



      {/* MODAL */}
      {activeJob && (
        <JobModal
          activeJob={activeJob}
          isEditing={isEditing}
          isNewJob={isNewJob}
          todayStr={todayStr}
          formatToYMD={formatToYMD}
          diffInDays={diffInDays}
          onClose={closeModal}
          onStartEdit={startEdit}
          onDelete={deleteJob}
          onSave={saveEdit}
          onCancel={cancelEdit}
          onChangeField={handleDraftChange}
          onFileChange={handleFileChange}
          onGenerateAI={generateAIKeywordsFromString}
        />
      )}
      <Analysis apps={apps} />

      <SkillTracker />
      <FollowUpNotes />
      {/* Dashboard row */}
      <ContactHeatmap />
    </div>
  );
};

export default AppsTable;
